import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {ListDetailRow, ListItem} from 'common/components/list-detail-row';
import {StringUtils} from 'common/utils/string-utils';
import {createUseStyles} from 'react-jss';
import {FdkText} from 'features/terms/components/fdk-text';
import {ConceptID, ConceptIDKey} from 'features/terms/felles-datakatalog/concept-ids';
import {Formuesgode} from 'features/rettsstiftelse/components/losore/formuesgode';
import {Formuesgode as FormuesgodeModel} from 'features/rettsstiftelse/model/losore/formuesgoder';
import {useLang} from 'features/i18n/use-lang';

const useFormuesgodeDetailRowStyles = createUseStyles({
  typeBeskrivelse: {
    fontWeight: 600,
    fontStyle: 'normal',
  },
});

const useAbandoneringStyles = createUseStyles({
  tidspunktForAbandonering: {
    marginBottom: '0.5rem',
  },
  abandoneringGroupList: {
    marginTop: 0,
    marginBottom: '2rem',
    paddingLeft: 0,

    '& > li': {
      listStyleType: 'none',
      margin: '0 0 0.5rem 0',
    },
  },
});

const AbandonedFormuesgoder: FC<{formuesgoder: FormuesgodeModel[]}> = ({formuesgoder}) => {
  const {t} = useTranslation(['rettsstiftelser']);
  const styles = useAbandoneringStyles();
  const lang = useLang();
  const formuesgodeDetailRowStyles = useFormuesgodeDetailRowStyles();

  const groupedByDate = formuesgoder.reduce<Record<string, FormuesgodeModel[]>>((groups, fg) => {
    if (fg.tidspunktForAbandonering) {
      if (!groups[fg.tidspunktForAbandonering]) {
        groups[fg.tidspunktForAbandonering] = [];
      }
      groups[fg.tidspunktForAbandonering].push(fg);
    }

    return groups;
  }, {});

  return (
    <ListDetailRow label={t('rettsstiftelser:formuesgoder.abandonering', {count: formuesgoder.length})}>
      {Object.entries(groupedByDate).map(([tidspunktForAbandonering, group]) => (
        <ListItem key={tidspunktForAbandonering}>
          <div className={styles.tidspunktForAbandonering}>
            <span className={formuesgodeDetailRowStyles.typeBeskrivelse}>
              {t('rettsstiftelser:formuesgoder.tidspunktForAbandonering')}
              {': '}
            </span>
            {StringUtils.formatDateTime(new Date(tidspunktForAbandonering), lang, t, true)}
          </div>
          <ul className={styles.abandoneringGroupList}>
            {group.map((fg, idx) => (
              <li key={idx}>
                <Formuesgode fg={fg} />
                {fg.eierandel && fg.eierandel?.teller !== fg.eierandel?.nevner && (
                  <div>{`${t('rettsstiftelser:eierandel')}: ${fg.eierandel.teller}/${fg.eierandel.nevner}`}</div>
                )}
              </li>
            ))}
          </ul>
        </ListItem>
      ))}
    </ListDetailRow>
  );
};

export const RegularFormuesgoder: FC<{formuesgoder: FormuesgodeModel[]; rsType: string}> = ({formuesgoder, rsType}) => {
  const {t} = useTranslation(['rettsstiftelser']);

  const context = rsType.split('.')[1];
  const label = t('rettsstiftelser:formuesgoder.formuesgode', {count: formuesgoder.length, context});
  const contextlessLabel = t('rettsstiftelser:formuesgoder.formuesgode', {count: formuesgoder.length});

  const conceptId =
    label === contextlessLabel ? ConceptID.FORMUESGODE : ConceptID[`FORMUESGODE_${context}` as unknown as ConceptIDKey];

  return (
    <ListDetailRow label={label} description={conceptId && <FdkText conceptId={conceptId} />}>
      {formuesgoder?.map((fg, idx) => (
        <ListItem key={idx}>
          <Formuesgode fg={fg} />
          {fg.eierandel && fg.eierandel?.teller !== fg.eierandel?.nevner && (
            <div>{`${t('rettsstiftelser:eierandel')}: ${fg.eierandel.teller}/${fg.eierandel.nevner}`}</div>
          )}
        </ListItem>
      ))}
    </ListDetailRow>
  );
};

export const FormuesgodeDetailRow: FC<{formuesgoder: FormuesgodeModel[]; rsType: string}> = ({
  formuesgoder,
  rsType,
}) => {
  const abandoneringer = formuesgoder.filter(fg => fg.tidspunktForAbandonering);
  const filteredFormuesgoder = formuesgoder.filter(fg => !fg.tidspunktForAbandonering);

  return (
    <>
      {!!filteredFormuesgoder?.length && <RegularFormuesgoder formuesgoder={filteredFormuesgoder} rsType={rsType} />}
      {!!abandoneringer?.length && <AbandonedFormuesgoder formuesgoder={abandoneringer} />}
    </>
  );
};
